export type TeamRole = 'admin' | 'manager' | 'member' | 'viewer';
export type TeamMemberStatus = 'active' | 'pending' | 'inactive';

export interface CompanySocialLinks { linkedin?: string; twitter?: string; facebook?: string; instagram?: string }

export interface CompanyNotificationPrefs {
  email: boolean;
  sms: boolean;
  push: boolean;
  inApp: boolean;
  frequency: 'instant' | 'daily' | 'weekly';
  categories: { team: boolean; subscription: boolean; aiTools: boolean; affiliate: boolean; feed: boolean; marketplace: boolean };
}

export interface CompanyAiPrefs {
  defaultModel: string;
  tokenBudget: number;
  alertThresholdPct: number;
  autoApproveReports: boolean;
  requireReviewForAI: boolean;
  privacyMode: boolean;
}

export interface CompanyAffiliatePrefs {
  defaultCommissionRate: number;
  cookieDurationDays: number;
  paymentThreshold: number;
  paymentMethod: 'paypal' | 'bank' | 'stripe';
  notificationEmails: string[];
  autoApprove: boolean;
}

export interface CompanySecurityPrefs {
  twoFactorAuth: boolean;
  sessionTimeout: number; // minutes
  ipWhitelist: string[];
  allowedDomains: string[];
}

export interface CompanyCompliancePrefs {
  gdpr: boolean;
  ccpa: boolean;
  cookieConsent: boolean;
  privacyPolicyUrl: string;
  termsUrl: string;
  dpaUrl: string;
}

export interface CompanyDataPrefs {
  dataRetentionDays: number;
  allowDataExport: boolean;
}

export interface CompanyCustomField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect';
  options?: string[];
  required: boolean;
  scope: 'company' | 'employees' | 'affiliates' | 'products';
}

export interface CompanyFeatureToggles {
  marketplace: boolean;
  affiliateProgram: boolean;
  aiTools: boolean;
  liveStreaming: boolean;
  debugMode: boolean;
}

export interface CompanyBrandingSettings {
  logo?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

export interface CompanySettings {
  companyId: string;
  companyName: string;
  industry: string;
  size: string;
  foundedYear?: number;
  website?: string;
  description?: string;
  socialLinks: CompanySocialLinks;
  branding: CompanyBrandingSettings;
  defaultRole: TeamRole;
  allowSelfRegistration: boolean;
  requireApprovalForNewMembers: boolean;
  notificationPreferences: CompanyNotificationPrefs;
  security: CompanySecurityPrefs;
  ai: CompanyAiPrefs;
  affiliate: CompanyAffiliatePrefs;
  compliance: CompanyCompliancePrefs;
  data: CompanyDataPrefs;
  customFields: CompanyCustomField[];
  features: CompanyFeatureToggles;
  loggingLevel: 'error' | 'warn' | 'info' | 'debug';
  updatedAt: string;
  updatedBy: string;
}

export type CompanySettingsPatch = Partial<Omit<CompanySettings, 'companyId' | 'updatedAt' | 'updatedBy'>>;

export interface TeamMember {
  id: string;
  companyId: string;
  userId: string;
  email: string;
  displayName: string;
  role: TeamRole;
  department?: string;
  title?: string;
  permissions: string[];
  status: TeamMemberStatus;
  invitedAt: string;
  joinedAt?: string;
  lastActive?: string;
}

export interface TeamMemberDraft { email: string; displayName: string; role: TeamRole; department?: string; title?: string }

export interface AuditLogEntry {
  id: string;
  companyId: string;
  userId: string;
  userName: string;
  action: string;
  details: string;
  ipAddress: string;
  createdAt: string;
}

export interface CompanyApiKey {
  id: string;
  name: string;
  keyPreview: string; // masked
  permissions: string[];
  createdAt: string;
  lastUsed?: string;
  expiresAt?: string;
}

export type IntegrationKey = 'slack' | 'zapier' | 'stripe' | 'hubspot' | 'salesforce';

export interface CompanyIntegration {
  key: IntegrationKey | string;
  name: string;
  description: string;
  connected: boolean;
  lastSync?: string;
  config: Record<string, string>;
}

export interface CompanyInvoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  status: 'paid' | 'due' | 'failed';
}

export interface CompanyPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isDefault: boolean;
}

export interface CompanySession {
  id: string;
  device: string;
  browser: string;
  ip: string;
  location: string;
  lastActive: string;
  current: boolean;
}

/** Permission catalog used by role editor. */
export const PERMISSIONS: { id: string; label: string }[] = [
  { id: 'can_manage_users', label: 'Manage users' },
  { id: 'can_edit_settings', label: 'Edit settings' },
  { id: 'can_view_reports', label: 'View reports' },
  { id: 'can_manage_billing', label: 'Manage billing' },
  { id: 'can_manage_integrations', label: 'Manage integrations' },
  { id: 'can_manage_affiliates', label: 'Manage affiliates' },
  { id: 'can_export_data', label: 'Export data' },
];

export const ROLE_PERMISSIONS: Record<TeamRole, string[]> = {
  admin: PERMISSIONS.map((p) => p.id),
  manager: ['can_manage_users', 'can_view_reports', 'can_manage_affiliates', 'can_manage_integrations'],
  member: ['can_view_reports'],
  viewer: ['can_view_reports'],
};
